import React, { useState } from 'react';

interface NewAPIKeyModalProps {
  apiKey: {
    api_key: string;
    secret_key: string;
    auth_token: string;
  };
  onClose: () => void;
}

export const NewAPIKeyModal: React.FC<NewAPIKeyModalProps> = ({ apiKey, onClose }) => {
  const [copied, setCopied] = useState<string | null>(null);

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">New API Key Created</h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Success!
              </h3>
              <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                <p>
                  Your API key has been created successfully. Please save these credentials securely. 
                  You won't be able to see the full API key again.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              API Key
            </label>
            <div className="flex">
              <input
                type="text"
                readOnly
                value={apiKey.api_key}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md bg-gray-50 dark:bg-gray-900 font-mono text-sm"
              />
              <button
                onClick={() => copyToClipboard(apiKey.api_key, 'api_key')}
                className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700"
              >
                {copied === 'api_key' ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Secret Key
            </label>
            <div className="flex">
              <input
                type="text"
                readOnly
                value={apiKey.secret_key}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md bg-gray-50 dark:bg-gray-900 font-mono text-sm"
              />
              <button
                onClick={() => copyToClipboard(apiKey.secret_key, 'secret_key')}
                className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700"
              >
                {copied === 'secret_key' ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Authorization Token
            </label>
            <div className="flex">
              <input
                type="text"
                readOnly
                value={apiKey.auth_token}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md bg-gray-50 dark:bg-gray-900 font-mono text-sm"
              />
              <button
                onClick={() => copyToClipboard(apiKey.auth_token, 'auth_token')}
                className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700"
              >
                {copied === 'auth_token' ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
          <h4 className="text-sm font-medium text-blue-800 mb-2">Usage Examples:</h4>
          <div className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
            <div>
              <strong>Authorization Header:</strong>
              <code className="block bg-white dark:bg-gray-800 p-2 rounded mt-1 font-mono">
                Authorization: ApiKey {apiKey.api_key}
              </code>
            </div>
            <div>
              <strong>X-API-Key Header:</strong>
              <code className="block bg-white dark:bg-gray-800 p-2 rounded mt-1 font-mono">
                X-API-Key: {apiKey.api_key}
              </code>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            I've Saved My Keys
          </button>
        </div>
      </div>
    </div>
  );
};
